import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FolderArchive, Upload, Trash2, Download, Search, FileText, Image as ImageIcon,
  FileType2, Plus, Pencil, X, Loader2,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { WsPageTitle } from "./WorkspaceLayout";
import WorkspaceEditorModal from "../../components/admin/WorkspaceEditorModal";
import { SingleRichEditor } from "../../components/admin/RichEditor";
import { Field } from "../../components/admin/Fields";
import { Settings2 } from "lucide-react";
import CategoryManager, { CategoryBadge, CategorySelect, CategorySidebar, useCategories, matchCategoryFilter } from "../../components/workspace/CategoryManager";
import { formatDMY } from "../../components/ui/datetime-picker";

const DEFAULT_CATEGORIES = [
  "Fatura", "Sözleşme", "Resmi Belge", "Fotoğraf", "Araştırma", "Sunum", "Diğer",
];

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, x = Number(n);
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 ? 2 : 1)} ${u[i]}`;
}
function fmtDate(iso) {
  if (!iso) return "";
  return formatDMY(iso, true);
}
function iconFor(mime) {
  if ((mime || "").startsWith("image/")) return ImageIcon;
  if ((mime || "") === "application/pdf") return FileType2;
  return FileText;
}

const emptyForm = () => ({
  id: null, name: "", category: "", note: "", tags: [], file: null,
});

export default function Documents() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const { categories: userCategories } = useCategories("documents");

  // Merge user-defined categories with defaults for the filter dropdown
  const allCatNames = [
    ...new Set([...DEFAULT_CATEGORIES, ...userCategories.map((c) => c.name)]),
  ];

  const { data = [] } = useQuery({
    queryKey: ["workspace", "documents", q],
    queryFn: () => api.get("/admin/workspace/documents", { params: { q } }).then((r) => r.data),
  });

  // filterCat holds a category ID (sidebar-selected); subtree-aware matcher.
  const filtered = data.filter((d) => matchCategoryFilter(d.category, filterCat, userCategories));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["workspace", "documents"] });
    qc.invalidateQueries({ queryKey: ["workspace", "overview"] });
  };

  const openNew = () => { setForm(emptyForm()); setDirty(false); setModalOpen(true); };
  const openEdit = (d) => {
    setForm({ id: d.id, name: d.name || d.filename, category: d.category || "", note: d.note || "", tags: d.tags || [], file: null });
    setDirty(false);
    setModalOpen(true);
  };

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    if (!form.id && !form.file) { toast.error("Dosya seçin"); return; }
    setBusy(true);
    try {
      if (form.id) {
        // metadata update only
        await api.put(`/admin/workspace/documents/${form.id}`, {
          name: form.name, category: form.category, note: form.note, tags: form.tags,
        });
        toast.success("Belge güncellendi");
      } else {
        const fd = new FormData();
        fd.append("file", form.file);
        fd.append("name", form.name);
        fd.append("category", form.category);
        fd.append("note", form.note);
        fd.append("tags", (form.tags || []).join(","));
        await api.post("/admin/workspace/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${form.file.name} yüklendi`);
      }
      setDirty(false);
      setModalOpen(false);
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/admin/workspace/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Belge kalıcı olarak silinecek. Onaylıyor musunuz?")) return;
    try { await api.delete(`/admin/workspace/documents/${id}`); invalidate(); toast.success("Silindi"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (!t || form.tags.includes(t)) { setTagInput(""); return; }
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput("");
    setDirty(true);
  };

  return (
    <div data-testid="ws-documents">
      <WsPageTitle
        icon={FolderArchive}
        title="Belgelerim"
        sub="PDF, Word, resim ve diğer dosyaların güvenli arşivi."
        action={
          <>
            <Button variant="outline" size="sm" onClick={() => setCatMgrOpen(true)} data-testid="ws-doc-cat-mgr">
              <Settings2 size={13} className="mr-1" /> Kategoriler
            </Button>
            <Button onClick={openNew} data-testid="ws-doc-new"><Plus size={14} className="mr-1" />Yeni belge</Button>
          </>
        }
      />

      {/* Sidebar + content */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4" data-testid="ws-documents-layout">
        <CategorySidebar
          kind="documents"
          items={data}
          value={filterCat}
          onChange={setFilterCat}
          onManage={() => setCatMgrOpen(true)}
          accent="emerald"
          allLabel="Tüm Belgeler"
        />

        <div className="min-w-0 space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-2xl border border-emerald-100 p-3 shadow-sm flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={13} className="absolute left-2.5 top-2.5 text-stone-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Belgelerde ara..." className="pl-7 h-9 text-sm" data-testid="ws-doc-search" />
            </div>
          </div>

          {/* Grid */}
          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-emerald-200 p-10 text-center text-stone-500">
              <FolderArchive size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Belge yok.</p>
              <Button onClick={openNew} className="mt-3" size="sm"><Plus size={13} className="mr-1" />İlk belgeyi yükle</Button>
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ws-docs-grid">
              {filtered.map((d) => {
                const Icon = iconFor(d.mime);
                return (
                  <li key={d.id} className="bg-white rounded-xl border border-emerald-100 p-3 shadow-sm flex items-start gap-3 hover:shadow-md transition" data-testid={`ws-doc-item-${d.id}`}>
                    <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 text-white flex items-center justify-center shrink-0 shadow">
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" title={d.name || d.filename}>{d.name || d.filename}</p>
                      {d.category && <span className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5 inline-block mt-0.5">{d.category}</span>}
                      <p className="text-[11px] text-stone-500 mt-0.5">{fmtBytes(d.size)} · {fmtDate(d.uploaded_at)}</p>
                      {d.note && (
                        <div
                          className="rich-content text-xs text-stone-700 mt-1 line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: d.note }}
                        />
                      )}
                      {d.tags?.length > 0 && (
                        <div className="flex gap-1 flex-wrap mt-1">
                          {d.tags.slice(0, 4).map((t, i) => <span key={i} className="text-[10px] bg-stone-50 text-stone-700 border border-stone-200 rounded-full px-1.5 py-0.5">#{t}</span>)}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-2 border-t pt-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(d)} data-testid={`ws-doc-edit-${d.id}`}><Pencil size={11} className="mr-1" />Düzenle</Button>
                        <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`ws-doc-dl-${d.id}`}><Download size={11} className="mr-1" />İndir</Button>
                        <Button size="sm" variant="outline" onClick={() => remove(d.id)} className="text-red-700 ml-auto" data-testid={`ws-doc-del-${d.id}`}><Trash2 size={11} /></Button>
                      </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
        </div>
      </div>

      {/* Modal */}
      <WorkspaceEditorModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setDirty(false); }}
        title={form.id ? "Belgeyi düzenle" : "Yeni belge yükle"}
        icon={FolderArchive}
        size="lg"
        busy={busy}
        dirty={dirty}
        onSubmit={save}
        submitLabel={form.id ? "Güncelle" : "Yükle"}
        testid="ws-doc-modal"
        accent="emerald"
      >
        <div className="space-y-3">
          {!form.id && (
            <Field label="Dosya *">
              <input
                type="file"
                onChange={(e) => setField("file", e.target.files?.[0] || null)}
                className="block w-full text-sm text-stone-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                data-testid="ws-doc-file"
              />
              <p className="text-[11px] text-stone-500 mt-1">En fazla 50 MB. İzinli: resim, PDF, Word, Excel, metin, zip.</p>
            </Field>
          )}

          <Field label="Belge adı">
            <Input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Dosyaya anlamlı bir ad verin"
              data-testid="ws-doc-name"
            />
          </Field>

          <Field label="Kategori">
            <CategorySelect
              value={form.category}
              onChange={(v) => setField("category", v)}
              kind="documents"
              testid="ws-doc-category"
              onManage={() => setCatMgrOpen(true)}
            />
            {DEFAULT_CATEGORIES.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5" data-testid="ws-doc-default-cats">
                {DEFAULT_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setField("category", c)}
                    className={`text-[10px] rounded-full px-2 py-0.5 border ${form.category === c ? "bg-emerald-100 border-emerald-300 text-emerald-800 font-semibold" : "bg-white border-stone-200 text-stone-500 hover:bg-stone-50"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </Field>

          <Field label="Açıklama">
            <SingleRichEditor
              value={form.note}
              onChange={(v) => setField("note", v)}
              testid="ws-doc-note"
              minH="min-h-[160px]"
            />
          </Field>

          <Field label="Etiketler">
            <div className="flex items-center gap-1 flex-wrap p-2 border rounded-md bg-white min-h-[42px]">
              {form.tags.map((t, i) => (
                <span key={i} className="text-[11px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                  #{t}
                  <button type="button" onClick={() => { setForm((f) => ({ ...f, tags: f.tags.filter((_, j) => j !== i) })); setDirty(true); }} className="hover:text-red-600"><X size={10} /></button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="etiket ekle…"
                className="text-[12px] outline-none bg-transparent flex-1 min-w-[100px]"
                data-testid="ws-doc-tag-input"
              />
            </div>
          </Field>
        </div>
      </WorkspaceEditorModal>

      <CategoryManager
        open={catMgrOpen}
        onClose={() => setCatMgrOpen(false)}
        kind="documents"
        title="Belge kategorilerini yönet"
      />
    </div>
  );
}
